import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Crown, KeyRound, ArrowRight } from 'lucide-react'
import { useProfile } from '@/context/ProfileContext'

export default function RedeemCode() {
  const { profile, redeemInviteCode } = useProfile()
  const navigate = useNavigate()
  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)

  // Already premium — nothing to redeem
  if (profile?.isPremium && !success) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="glass-panel p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-[var(--gold)]/15 flex items-center justify-center mx-auto mb-4">
            <Crown className="w-7 h-7 text-[var(--gold)]" aria-hidden="true" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-[var(--gold)] mb-2">
            You Already Have Premium
          </h1>
          <p className="text-[var(--text-muted)] text-sm mb-6">
            Your account already has full premium access. Head to the chat to get started.
          </p>
          <button
            onClick={() => navigate('/home')}
            className="w-full bg-[var(--gold)] text-[var(--text-on-gold)] font-bold py-3 rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2"
          >
            Go to Chat <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="glass-panel p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-[var(--gold)]/15 flex items-center justify-center mx-auto mb-4">
            <Crown className="w-8 h-8 text-[var(--gold)]" aria-hidden="true" />
          </div>
          <div className="inline-flex items-center gap-1.5 text-[var(--gold)] mb-3">
            <KeyRound className="w-4 h-4" aria-hidden="true" />
            <span className="text-xs font-bold uppercase tracking-wider">Access Granted</span>
          </div>
          <h1 className="font-heading text-2xl font-bold text-[var(--text-primary)] mb-2">
            Lifetime Premium Activated
          </h1>
          <p className="text-[var(--text-muted)] text-sm mb-6">
            Welcome. You now have full, unlimited Premium access to Illuminate Faith — forever.
            Let's get your account set up so the AI knows how to serve you best.
          </p>
          <button
            onClick={() => navigate('/onboarding')}
            className="w-full bg-[var(--gold)] text-[var(--text-on-gold)] font-bold py-3 rounded-xl hover:opacity-90 transition-all flex items-center justify-center gap-2"
          >
            Continue to Setup <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    )
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!code.trim()) return
    setLoading(true)
    setError('')
    try {
      const result = await redeemInviteCode(code.trim())
      if (result.error) {
        setError(result.error)
      } else {
        setSuccess(true)
      }
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen px-4">
      <div className="glass-panel p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <h1 className="font-heading text-2xl font-bold text-[var(--gold)] mb-1">
            Enter Your Access Code
          </h1>
          <p className="text-[var(--text-muted)] text-sm">
            Redeem your invite code to unlock lifetime premium access.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="text"
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            aria-label="Premium invite code"
            placeholder="XXXXXXXX"
            maxLength={12}
            autoFocus
            className="w-full px-4 py-3 rounded-xl bg-[var(--bg-surface)] border border-[var(--glass-border)] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] text-center tracking-[0.3em] font-mono text-xl focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
          />
          {error && <p className="text-red-400 text-sm text-center">{error}</p>}
          <button
            type="submit"
            disabled={loading || !code.trim()}
            className="w-full bg-[var(--gold)] text-[var(--text-on-gold)] font-bold py-3 rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              'Checking...'
            ) : (
              <>
                Redeem Code <Crown className="w-4 h-4" aria-hidden="true" />
              </>
            )}
          </button>
        </form>

        <p className="text-center text-[var(--text-muted)] text-xs mt-5">
          Don't have a code?{' '}
          <a
            href="mailto:hello@illuminatefaithai.com"
            className="text-[var(--gold)] hover:underline"
          >
            Contact us for an invite
          </a>
        </p>
      </div>
    </div>
  )
}
